/**
 * Quality tiers. Starts high on desktop and medium on phones, then steps down if the frame rate
 * stays low. It only steps down, so quality doesn't flicker. `?hq` in the URL locks the top tier.
 */
export interface Tier {
  name: 'high' | 'medium' | 'low' | 'lowest';
  pixelRatioCap: number;
  /** 0 turns shadows off. */
  shadowMapSize: number;
}

/**
 * Sharpness goes last: a slow device first loses shadow detail, then shadows, and only then pixels.
 * Phones start on 'medium', which is still drawn at 2× (sharp on a phone screen).
 */
export const TIERS: readonly Tier[] = [
  { name: 'high', pixelRatioCap: 2, shadowMapSize: 2048 },
  { name: 'medium', pixelRatioCap: 2, shadowMapSize: 1024 },
  { name: 'low', pixelRatioCap: 1.75, shadowMapSize: 0 },
  { name: 'lowest', pixelRatioCap: 1.25, shadowMapSize: 0 },
];

const WINDOW_SECONDS = 1.5;
/** Below this frame rate a window counts as slow. Phones accept a little less so they stay sharp. */
const LOW_FPS = { desktop: 48, phone: 40 } as const;
/** Ignore the first seconds while textures upload and shaders compile. */
const WARMUP_SECONDS = 3;

/** Touch devices: a weaker GPU and a very dense screen, so they start lower and step down faster. */
export function isPhone(): boolean {
  return matchMedia('(pointer: coarse)').matches || Math.min(screen.width, screen.height) < 600;
}

export class PerfGovernor {
  private index: number;
  private readonly locked: boolean;
  private elapsed = 0;
  private frames = 0;
  private warmup = WARMUP_SECONDS;
  private slowWindows = 0;
  /** Slow windows in a row before stepping down. */
  private readonly patience = 2;
  private readonly lowFps: number;

  constructor(private readonly apply: (tier: Tier) => void) {
    this.locked = new URLSearchParams(location.search).has('hq');
    const phone = isPhone();
    this.lowFps = phone ? LOW_FPS.phone : LOW_FPS.desktop;
    this.index = this.locked || !phone ? 0 : 1;
    this.apply(this.tier);
  }

  get tier(): Tier {
    return TIERS[this.index] ?? TIERS[0]!;
  }

  /** Call once per rendered frame with the real frame time. */
  frame(dt: number): void {
    if (this.locked) return;
    if (this.warmup > 0) {
      this.warmup -= dt;
      return;
    }
    this.elapsed += dt;
    this.frames += 1;
    if (this.elapsed < WINDOW_SECONDS) return;
    const fps = this.frames / this.elapsed;
    this.elapsed = 0;
    this.frames = 0;
    this.slowWindows = fps < this.lowFps ? this.slowWindows + 1 : 0;
    if (this.slowWindows >= this.patience && this.index < TIERS.length - 1) {
      this.index += 1;
      this.slowWindows = 0;
      this.warmup = 1;
      this.apply(this.tier);
    }
  }
}
